import React from "react";
import useTranslation from "next-translate/useTranslation";
import Script from "next/script";
import { useRouter } from "next/router";
import { generateId } from "@/util/generateId";
import FBScript from "@/components/FBScript";
import { RegisterStep, useRegisterForm } from "./useRegisterForm";
import { FormStep, UsernameStep, JoinedStep } from "./RegisterSteps";

export default function RegisterPageContent({
  onStepChange,
  onExistingUserSignIn,
}: {
  onStepChange: (step: RegisterStep) => void;
  onExistingUserSignIn?: () => void;
}) {
  const { t } = useTranslation("common");
  const { t: registerT } = useTranslation("auth");
  const router = useRouter();

  const {
    username,
    email,
    password,
    news,
    step,
    signedUpWithEmail,
    usernameError,
    emailStyle,
    passwordStyle,
    usernameStyle,
    guestElo,
    setNews,
    emailChange,
    usernameChange,
    passwordChange,
    advanceToUsernameStep,
    validateCreateAccount,
    createSocialAccount,
    signInFBInline,
    signInAppleWebInline,
    initializeGoogleButton,
  } = useRegisterForm({
    onExistingUserSignIn:
      onExistingUserSignIn ||
      (() => {
        router.push("/lobby");
      }),
    onStepChange,
  });

  return (
    <>
      <FBScript />
      <Script
        onLoad={() => {
          //@ts-ignore
          window.AppleID?.auth?.init?.({
            clientId: "com.chesshotel.service",
            scope: "",
            redirectURI:
              "https://www.chesshotel.com/api/v1/auth/apple/redirect",
            state: "Eksätravägen975031",
            nonce: generateId(),
            usePopup: true,
          });
        }}
        src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
      />
      <Script
        src={`https://accounts.google.com/gsi/client?hl=${router.locale}`}
        strategy="afterInteractive"
        onLoad={() => {
          console.log("Google Identity Services script loaded");
          initializeGoogleButton();
        }}
      />
      {step === "joined" ? (
        <JoinedStep
          t={t}
          registerT={registerT}
          signedUpWithEmail={signedUpWithEmail}
          router={router}
        />
      ) : step === "username" ? (
        <UsernameStep
          t={t}
          registerT={registerT}
          username={username}
          usernameStyle={usernameStyle}
          usernameError={usernameError}
          onUsernameChange={usernameChange}
          onSubmit={validateCreateAccount}
        />
      ) : step === "socialUsername" ? (
        <UsernameStep
          t={t}
          registerT={registerT}
          username={username}
          usernameStyle={usernameStyle}
          usernameError={usernameError}
          onUsernameChange={usernameChange}
          onSubmit={createSocialAccount}
        />
      ) : (
        <FormStep
          t={t}
          registerT={registerT}
          guestElo={guestElo}
          email={email}
          password={password}
          emailStyle={emailStyle}
          passwordStyle={passwordStyle}
          onEmailChange={emailChange}
          onPasswordChange={passwordChange}
          onSetNews={setNews}
          onSubmit={advanceToUsernameStep}
          onSignInFB={signInFBInline}
          onSignInApple={signInAppleWebInline}
        />
      )}
    </>
  );
}
